import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, MicIcon, StopCircleIcon, RefreshCwIcon } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useAISalesAssistant } from '../../contexts/AISalesAssistantContext';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
export const ChatTab: React.FC = () => {
  const {
    messages,
    sendMessage,
    clearChat
  } = useChat();
  const {
    setActiveFeature
  } = useAISalesAssistant();
  const {
    customerProfile,
    closingReadiness
  } = useClosingAssistant();
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  // Focus input when component mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };
  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };
  const toggleVoiceRecording = () => {
    setIsRecording(!isRecording);
    // Simulate voice recording
    if (!isRecording) {
      setTimeout(() => {
        setIsRecording(false);
        setInputValue('I have a customer looking for a 10-day Europe tour for a family of 4 with a budget of $3000');
        inputRef.current?.focus();
      }, 3000);
    }
  };
  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  // Generate smart suggestions based on closing readiness
  const getSmartSuggestions = () => {
    if (closingReadiness === 'high') {
      return ['Create quote for this package', 'Show availability for next month', 'Compare with similar packages'];
    } else if (closingReadiness === 'medium') {
      return ['Tell me more about included activities', 'What are the payment options?', 'Show customer testimonials'];
    } else {
      return ['Show family-friendly tours', 'Find options under $3000', 'Compare destinations'];
    }
  };
  return <div className="flex flex-col h-full" data-id="element-169">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-170">
        {messages.map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-171">
            <div className={`max-w-[80%] rounded-lg p-3 ${message.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-172">
              <p className="text-sm" data-id="element-173">{message.content}</p>
              {/* Product recommendations */}
              {message.products && message.products.length > 0 && <div className="mt-3 space-y-3" data-id="element-174">
                  {message.products.map(product => <div key={product.id} className="bg-white rounded-md overflow-hidden shadow-sm border border-gray-200" data-id="element-175">
                      <div className="flex" data-id="element-176">
                        <img src={product.image} alt={product.title} className="h-20 w-20 object-cover" data-id="element-177" />
                        <div className="p-2 flex-1" data-id="element-178">
                          <h4 className="font-medium text-gray-800 text-sm" data-id="element-179">
                            {product.title}
                          </h4>
                          <div className="flex justify-between items-center mt-1" data-id="element-180">
                            <p className="text-xs text-gray-600" data-id="element-181">
                              {product.destination} • {product.duration}
                            </p>
                            <p className="text-xs font-bold text-blue-600" data-id="element-182">
                              ${product.price}
                            </p>
                          </div>
                          {product.confidence && <div className="mt-1 flex items-center" data-id="element-183">
                              <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden" data-id="element-184">
                                <div className="h-full bg-green-500 rounded-full" style={{
                        width: `${product.confidence * 100}%`
                      }} data-id="element-185"></div>
                              </div>
                              <span className="text-xs text-gray-500 ml-2" data-id="element-186">
                                {Math.round(product.confidence * 100)}% match
                              </span>
                            </div>}
                        </div>
                      </div>
                    </div>)}
                  <button className="text-xs text-blue-600 font-medium" data-id="element-187">
                    View all matches
                  </button>
                </div>}
              {/* Suggestions */}
              {message.suggestions && message.suggestions.length > 0 && <div className="mt-3 flex flex-wrap gap-2" data-id="element-188">
                  {message.suggestions.map((suggestion, index) => <button key={index} className="text-xs bg-white text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-50" onClick={() => handleSuggestionClick(suggestion)} data-id="element-189">
                      {suggestion}
                    </button>)}
                </div>}
              <div className="mt-1 text-right" data-id="element-190">
                <span className={`text-xs ${message.sender === 'user' ? 'text-blue-200' : 'text-gray-500'}`} data-id="element-191">
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>
            </div>
          </div>)}
        <div ref={messagesEndRef} data-id="element-192" />
      </div>
      {/* Input area */}
      <div className="border-t p-3" data-id="element-193">
        <form onSubmit={handleSubmit} className="flex items-center space-x-2" data-id="element-194">
          <button type="button" className={`p-2 rounded-full ${isRecording ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200`} onClick={() => setActiveFeature('audio')} data-id="element-195">
            <MicIcon className="h-5 w-5" data-id="element-196" />
          </button>
          <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Type your message..." data-id="element-197" />
          {inputValue.trim() ? <button type="submit" className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700" data-id="element-198">
              <SendIcon className="h-5 w-5" data-id="element-199" />
            </button> : <button type="button" className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200" onClick={() => clearChat()} data-id="element-200">
              <RefreshCwIcon className="h-5 w-5" data-id="element-201" />
            </button>}
        </form>
        {/* Smart suggestions based on closing readiness */}
        <div className="mt-2 flex flex-wrap gap-2" data-id="element-202">
          {getSmartSuggestions().map((suggestion, index) => <button key={index} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gray-200" onClick={() => setInputValue(suggestion)} data-id="element-203">
              {suggestion}
            </button>)}
        </div>
        {/* Closing readiness indicator */}
        {customerProfile.readinessScore > 0 && <div className="mt-3 flex items-center justify-between text-xs p-2 rounded-md bg-gray-50" data-id="element-204">
            <span className="text-gray-600" data-id="element-205">Customer readiness:</span>
            <div className="flex items-center" data-id="element-206">
              <div className="w-20 h-1.5 bg-gray-200 rounded-full mr-2" data-id="element-207">
                <div className={`h-1.5 rounded-full ${customerProfile.readinessScore >= 70 ? 'bg-green-500' : customerProfile.readinessScore >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${customerProfile.readinessScore}%`
            }} data-id="element-208"></div>
              </div>
              <button onClick={() => setActiveFeature('closing')} className="text-blue-600 hover:underline" data-id="element-209">
                Details
              </button>
            </div>
          </div>}
      </div>
    </div>;
};